import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import AiConversationsIcon from "../UuiNavigation/icons/AiConversationsIcon";
import AiPromptTemplatesIcon from "../UuiNavigation/icons/AiPromptTemplatesIcon";
import AiTemplatesIcon from "../UuiNavigation/icons/AiTemplatesIcon";
import AppointmentsIcon from "../UuiNavigation/icons/AppointmentsIcon";
import ArticlesIcon from "../UuiNavigation/icons/ArticlesIcon";
import BotTasksIcon from "../UuiNavigation/icons/BotTasksIcon";
import CampaignsIcon from "../UuiNavigation/icons/CampaignsIcon";
import ConnectionsIcon from "../UuiNavigation/icons/ConnectionsIcon";
import ContactsIcon from "../UuiNavigation/icons/ContactsIcon";
import CompaniesIcon from "../UuiNavigation/icons/CompaniesIcon";
import GoEngageCampaignIcon from "../UuiNavigation/icons/GoEngageCampaignIcon";
import HomeIcon from "../UuiNavigation/icons/HomeIcon";
import IntentsIcon from "../UuiNavigation/icons/IntentsIcon";
import ListsIcon from "../UuiNavigation/icons/ListsIcon";
import ModelsIcon from "../UuiNavigation/icons/ModelsIcon";
import ReportsIcon from "../UuiNavigation/icons/ReportsIcon";
import SearchIcon from "../UuiNavigation/icons/SearchIcon";
import SettingsIcon from "../UuiNavigation/icons/SettingsIcon";
import VisitorsIcon from "../UuiNavigation/icons/VisitorsIcon";
import WebChatsIcon from "../UuiNavigation/icons/WebChatsIcon";
import SalesCoachIcon from "../UuiNavigation/icons/SalesCoachIcon";
import AiAgentsIcon from "../UuiNavigation/icons/AiAgentsIcon";
import AiAgentInboxIcon from "../UuiNavigation/icons/AiAgentInboxIcon";
import CanvasIcon from "../UuiNavigation/icons/CanvasIcon";
import FormsIcon from "../UuiNavigation/icons/FormsIcon";
import { menuItems } from "./NavItems";
import SequencerIcon from "../UuiNavigation/icons/SequencerIcon";
import EnrichmentIcon from "../UuiNavigation/icons/EnrichmentIcon";
import PhoneIcon from "../UuiNavigation/icons/PhoneIcon";

const iconMapping: { [key: string]: JSX.Element } = {
  search: <SearchIcon stroke="#9ca3af" />,
  contacts: <ContactsIcon stroke="#9ca3af" />,
  companies: <CompaniesIcon stroke="#9ca3af" />,
  lists: <ListsIcon stroke="#9ca3af" />,
  intents: <IntentsIcon stroke="#9ca3af" />,
  webchats: <WebChatsIcon stroke="#9ca3af" />,
  campaigns: <CampaignsIcon stroke="#9ca3af" />,
  engage_campaign: <GoEngageCampaignIcon stroke="#9ca3af" />,
  bot_tasks: <BotTasksIcon stroke="#9ca3af" />,
  appointments: <AppointmentsIcon stroke="#9ca3af" />,
  visitors: <VisitorsIcon stroke="#9ca3af" />,
  connections: <ConnectionsIcon stroke="#9ca3af" />,
  models: <ModelsIcon stroke="#9ca3af" />,
  ai_conversations: <AiConversationsIcon stroke="#9ca3af" />,
  ai_templates: <AiTemplatesIcon stroke="#9ca3af" />,
  ai_prompt_templates: <AiPromptTemplatesIcon stroke="#9ca3af" />,
  articles: <ArticlesIcon stroke="#9ca3af" />,
  reports: <ReportsIcon stroke="#9ca3af" />,
  settings: <SettingsIcon stroke="#9ca3af" />,
  home: <HomeIcon stroke="#9ca3af" />,
  sales_coach: <SalesCoachIcon stroke="#9ca3af" />,
  ai_agents: <AiAgentsIcon stroke="#9ca3af" />,
  ai_agent_inbox: <AiAgentInboxIcon stroke="#9ca3af" />,
  canvas: <CanvasIcon stroke="#9ca3af" />,
  forms: <FormsIcon stroke="#9ca3af" />,
  sequencer: <SequencerIcon stroke="#9ca3af" />,
  enrichment: <EnrichmentIcon stroke="#9ca3af" />,
  phone: <PhoneIcon stroke="#9ca3af" />,
};

export const getCookie = (name: any): string | undefined => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift();
  return undefined;
};

export default function UuiNavigation() {
  const uuiBaseURL = "https://uui.cience.com";
  const [uuiAppId, setUuiAppId] = useState<string | null>(null);
  const [uuiAiType, setUuiAiType] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    const appId = getCookie("uuiAppId");
    const uuiAiType = getCookie("aiType");
    setUuiAppId(appId ?? null);
    setUuiAiType(uuiAiType ?? null);
  }, []);

  let loginUrl: string | undefined = undefined;

  if (!uuiAppId) {
    loginUrl = `${uuiBaseURL}/agents/sign_in`;
  }
  const uuiAppPath = `/apps/${uuiAppId}`;
  const aiTypeString = uuiAiType ?? "0";

  return (
    <div className="relative">
      {/* Toggle Button - Outside the scrollable container */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="border-[#dfdfdf] dark:border-[#292929] absolute top-14 -right-2 z-50 flex h-[34px] w-[16px] cursor-pointer items-center justify-center rounded-lg border bg-[#f9f9f9] text-[#7d2df3] transition-colors dark:bg-[#101010]"
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>

      <div className={`border-[#dfdfdf] dark:border-[#292929] border-r bg-default sticky top-0 hidden h-full overflow-y-scroll px-3 py-2 md:block scrollbar-hide font-light ${isCollapsed ? "w-[60px]" : "w-[206px]"}`}>
        <a
          key="home"
          data-test-id="Home"
          aria-label="Dashboard"
          className={`bg-gray-50  text-gray-950 dark:text-white  hover:bg-[#ddc5ff] dark:bg-[#0A0A0A] dark:hover:bg-[#7D2DF3] group my-5 flex h-10 cursor-pointer items-center rounded-[10px] text-sm transition ${isCollapsed ? "justify-center" : ""}`}
          href={uuiAppId ? uuiBaseURL + uuiAppPath : loginUrl}
          data-state="closed"
        >
          <div>{iconMapping["home"]}</div>
          {!isCollapsed && (
            <span className="ml-2 w-full justify-between truncate text-ellipsis lg:flex" data-testid="bookings-test">
              Home
            </span>
          )}
        </a>
        {menuItems(aiTypeString)
          .filter(item => !item.hidden)
          .map((item, index) => {
            if (item.type === "separator") {
              if (isCollapsed) {
                return null;
              }
              return (
                <div key={index} className="h-5">
                  <span className="nav-item text-[var(--graph8-text)] pl-1 text-sm uppercase tracking-wider">
                    {item.label}
                  </span>
                </div>
              );
            } else if (item.type === "link") {
              const href =
                item.name === "Go Engage" ||
                item.name === "Appointments" ||
                item.name === "Sales Coach" ||
                item.name === "Agent Inbox" ||
                item.name === "AI Canvas" ||
                item.name === "AI Inbox" ||
                item.name === "Dialer"
                  ? uuiBaseURL + "/auth/" + uuiAppId + item.path
                  : uuiAppId
                  ? uuiBaseURL + uuiAppPath + item.path
                  : loginUrl;

              if (item.name === "Connections") {
                return (
                  <div
                    key={index}
                    data-test-id={item.name}
                    aria-label={item.tip}
                    className={`text-primary dark:!text-white todesktop:hover:bg-transparent bg-[#7d2df31a] dark:bg-[--brand-default] group my-2 flex h-10 cursor-pointer items-center rounded-[10px] text-sm  transition ${isCollapsed ? "justify-center" : ""}`}
                    data-state="closed"
                  >
                    <div>{iconMapping[item.icon]}</div>
                    {!isCollapsed && (
                      <span
                        className="ml-2 w-full justify-between truncate text-ellipsis lg:flex"
                        data-testid="bookings-test"
                      >
                        {item.name}
                      </span>
                    )}
                  </div>
                );
              }

              return (
                <a
                  key={index}
                  data-test-id={item.name}
                  aria-label={item.tip}
                  className={`hover:bg-[#ddc5ff] dark:hover:bg-[--brand-default] dark:hover:text-white group my-2 flex h-10 items-center rounded-[10px] text-sm transition dark:bg-[#0a0a0a] text-black dark:text-white ${isCollapsed ? "justify-center" : ""}`}
                  data-state="closed"
                  href={href}
                >
                  <div>{iconMapping[item.icon]}</div>
                  {!isCollapsed && (
                    <div className="ml-2 w-full truncate text-ellipsis lg:flex" data-testid="bookings-test">
                      {item.name}
                    </div>
                  )}
                </a>
              );
            }
            return null;
          })}
      </div>
    </div>
  );
}
